import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Settings, Lock, Unlock, Eye, Users, Shield, Check, X, Loader2, 
  Building2, Clock, Trash2, AlertTriangle, Link2, Copy, Plus, ExternalLink,
  UserPlus, Crown, Mail, Calendar, CalendarCheck, MessageCircle, Phone,
  RotateCcw, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
  'Content-Type': 'application/json'
});

const CompanySettingsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { isUSA } = useLocale();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam && ['services', 'privacy', 'authorizations', 'team', 'preferences'].includes(tabParam) 
      ? tabParam 
      : 'services';
  });
  
  // Update active tab when URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['services', 'privacy', 'authorizations', 'team', 'preferences'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  
  // Privacy state
  const [company, setCompany] = useState(null);
  const [catalogPrivate, setCatalogPrivate] = useState(false);
  const [hideContactData, setHideContactData] = useState(false);
  const [acceptsScheduling, setAcceptsScheduling] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [authorizedViewers, setAuthorizedViewers] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [linkDialog, setLinkDialog] = useState({ open: false });
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [newLinkExpires, setNewLinkExpires] = useState('30');
  const [requestDialog, setRequestDialog] = useState({ open: false, request: null });
  const [contactAccessRequests, setContactAccessRequests] = useState([]);
  const [catalogAccessRequests, setCatalogAccessRequests] = useState([]);
  const [processingRequest, setProcessingRequest] = useState(null);
  
  // Authorizations given state (companies we've granted access to)
  const [givenContactAuthorizations, setGivenContactAuthorizations] = useState([]);
  const [givenCatalogAuthorizations, setGivenCatalogAuthorizations] = useState([]);
  const [revokingAuthorization, setRevokingAuthorization] = useState(null);

  // Services offered state
  const [offeredServices, setOfferedServices] = useState([]);
  const [newService, setNewService] = useState('');

  // Team state
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [processing, setProcessing] = useState(false);

  const isAdmin = members.find(m => m.user_id === user?.user_id)?.is_admin || members.length === 0;

  useEffect(() => {
    let isMounted = true;
    
    const fetchAllData = async () => {
      if (!user?.company_id) {
        setLoading(false);
        return;
      }
      
      try {
        const companyRes = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        if (isMounted && companyRes.ok) {
          const companyData = await companyRes.json();
          setCompany(companyData);
          setCatalogPrivate(companyData.catalog_private || false);
          setHideContactData(companyData.hide_contact_data || false);
          setAcceptsScheduling(companyData.accepts_scheduling || false);
          setWhatsappEnabled(companyData.whatsapp_enabled || false);
          setWhatsappNumber(companyData.whatsapp_number || '');
          setOfferedServices(companyData.offered_services || []);
        }

        // Fetch other data
        const [requestsRes, viewersRes, linksRes] = await Promise.all([
          fetch(`${API_URL}/api/companies/${user.company_id}/access-requests`, { headers: getAuthHeaders(), credentials: 'include' }),
          fetch(`${API_URL}/api/companies/${user.company_id}/authorized-viewers`, { headers: getAuthHeaders(), credentials: 'include' }),
          fetch(`${API_URL}/api/companies/${user.company_id}/share-links`, { headers: getAuthHeaders(), credentials: 'include' })
        ]);

        if (isMounted && requestsRes.ok) setPendingRequests(await requestsRes.json());
        if (isMounted && viewersRes.ok) setAuthorizedViewers(await viewersRes.json());
        if (isMounted && linksRes.ok) setShareLinks(await linksRes.json());

        // Fetch contact and catalog access requests
        const [contactReqRes, catalogReqRes] = await Promise.all([
          fetch(`${API_URL}/api/my-contact-requests`, { headers: getAuthHeaders(), credentials: 'include' }),
          fetch(`${API_URL}/api/my-catalog-access`, { headers: getAuthHeaders(), credentials: 'include' })
        ]);
        
        if (isMounted && contactReqRes.ok) setContactAccessRequests(await contactReqRes.json());
        if (isMounted && catalogReqRes.ok) {
          const catalogData = await catalogReqRes.json();
          setCatalogAccessRequests(catalogData.pending_requests || []);
        }

        // Fetch given authorizations (companies we've granted access to)
        const [givenContactRes, givenCatalogRes] = await Promise.all([
          fetch(`${API_URL}/api/my-given-contact-authorizations`, { headers: getAuthHeaders(), credentials: 'include' }),
          fetch(`${API_URL}/api/my-given-catalog-authorizations`, { headers: getAuthHeaders(), credentials: 'include' })
        ]);
        
        if (isMounted && givenContactRes.ok) setGivenContactAuthorizations(await givenContactRes.json());
        if (isMounted && givenCatalogRes.ok) setGivenCatalogAuthorizations(await givenCatalogRes.json());

        // Fetch team data
        const [membersRes, invitesRes, permsRes] = await Promise.all([
          fetch(`${API_URL}/api/team/members`, { headers: getAuthHeaders(), credentials: 'include' }),
          fetch(`${API_URL}/api/team/invitations`, { headers: getAuthHeaders(), credentials: 'include' }),
          fetch(`${API_URL}/api/team/permissions`, { headers: getAuthHeaders(), credentials: 'include' })
        ]);

        if (isMounted && membersRes.ok) setMembers(await membersRes.json());
        if (isMounted && invitesRes.ok) setInvitations(await invitesRes.json());
        if (isMounted && permsRes.ok) setPermissions(await permsRes.json());
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    fetchAllData();
    
    return () => { isMounted = false; };
  }, [user?.company_id]);

  // Handlers
  const handleToggleCatalogPrivacy = async () => {
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ catalog_private: !catalogPrivate })
      });
      if (res.ok) {
        setCatalogPrivate(!catalogPrivate);
        toast.success(isUSA 
          ? `Catalog is now ${!catalogPrivate ? 'private' : 'public'}`
          : `Catálogo ahora es ${!catalogPrivate ? 'privado' : 'público'}`);
      }
    } catch (error) {
      toast.error(isUSA ? 'Error changing privacy' : 'Error al cambiar privacidad');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleHideContactData = async () => {
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ hide_contact_data: !hideContactData })
      });
      if (res.ok) {
        setHideContactData(!hideContactData);
        toast.success(isUSA
          ? (hideContactData ? 'Contact data visible' : 'Contact data hidden')
          : (hideContactData ? 'Datos de contacto visibles' : 'Datos de contacto ocultos'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error changing settings' : 'Error al cambiar configuración');
    } finally {
      setUpdating(false);
    }
  };

  // Handle contact access request approval/rejection
  const handleContactAccessResponse = async (requestId, action) => {
    setProcessingRequest(requestId);
    try {
      const res = await fetch(`${API_URL}/api/contact-access/${requestId}/respond`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ action })
      });
      
      if (res.ok) {
        setContactAccessRequests(prev => prev.filter(r => r.request_id !== requestId));
        toast.success(isUSA 
          ? `Request ${action === 'approve' ? 'approved' : 'rejected'}`
          : `Solicitud ${action === 'approve' ? 'aprobada' : 'rechazada'}`);
      } else {
        toast.error(isUSA ? 'Error processing request' : 'Error al procesar solicitud');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error processing request' : 'Error al procesar solicitud');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle catalog access request approval/rejection
  const handleCatalogAccessResponse = async (requestId, action) => {
    setProcessingRequest(requestId);
    try {
      const res = await fetch(`${API_URL}/api/catalog-access/${requestId}/respond`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status: action === 'approve' ? 'approved' : 'rejected' })
      });
      
      if (res.ok) {
        setCatalogAccessRequests(prev => prev.filter(r => r.request_id !== requestId));
        toast.success(isUSA 
          ? `Request ${action === 'approve' ? 'approved' : 'rejected'}`
          : `Solicitud ${action === 'approve' ? 'aprobada' : 'rechazada'}`);
      } else {
        toast.error(isUSA ? 'Error processing request' : 'Error al procesar solicitud');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error processing request' : 'Error al procesar solicitud');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRevokeContactAuthorization = async (requestId) => {
    setRevokingAuthorization(requestId);
    try {
      const res = await fetch(`${API_URL}/api/revoke-contact-authorization/${requestId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        setGivenContactAuthorizations(prev => prev.filter(a => a.request_id !== requestId));
        toast.success(isUSA ? 'Authorization revoked' : 'Autorización revocada');
      } else {
        toast.error(isUSA ? 'Error revoking authorization' : 'Error al revocar autorización');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error revoking authorization' : 'Error al revocar autorización');
    } finally {
      setRevokingAuthorization(null);
    }
  };

  const handleRevokeCatalogAuthorization = async (requestId) => {
    setRevokingAuthorization(requestId);
    try {
      const res = await fetch(`${API_URL}/api/revoke-catalog-authorization/${requestId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        setGivenCatalogAuthorizations(prev => prev.filter(a => a.request_id !== requestId));
        toast.success(isUSA ? 'Authorization revoked' : 'Autorización revocada');
      } else {
        toast.error(isUSA ? 'Error revoking authorization' : 'Error al revocar autorización');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error revoking authorization' : 'Error al revocar autorización');
    } finally {
      setRevokingAuthorization(null);
    }
  };

  const handleToggleScheduling = async () => {
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ accepts_scheduling: !acceptsScheduling })
      });
      if (res.ok) {
        setAcceptsScheduling(!acceptsScheduling);
        toast.success(isUSA
          ? (acceptsScheduling ? 'Scheduling disabled' : 'Scheduling enabled')
          : (acceptsScheduling ? 'Programación desactivada' : 'Programación activada'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error changing settings' : 'Error al cambiar configuración');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleWhatsapp = async () => {
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ whatsapp_enabled: !whatsappEnabled })
      });
      if (res.ok) {
        setWhatsappEnabled(!whatsappEnabled);
        toast.success(isUSA
          ? (whatsappEnabled ? 'WhatsApp disabled' : 'WhatsApp enabled')
          : (whatsappEnabled ? 'WhatsApp desactivado' : 'WhatsApp activado'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error changing settings' : 'Error al cambiar configuración');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveWhatsappNumber = async () => {
    if (!whatsappNumber.trim()) return;
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ whatsapp_number: whatsappNumber })
      });
      if (res.ok) {
        toast.success(isUSA ? 'WhatsApp number saved' : 'Número de WhatsApp guardado');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error saving number' : 'Error al guardar número');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddService = async () => {
    if (!newService.trim()) return;
    const updatedServices = [...offeredServices, newService.trim()];
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ offered_services: updatedServices })
      });
      if (res.ok) {
        setOfferedServices(updatedServices);
        setNewService('');
        toast.success(isUSA ? 'Service added' : 'Servicio agregado');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error adding service' : 'Error al agregar servicio');
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveService = async (index) => {
    const updatedServices = offeredServices.filter((_, i) => i !== index);
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${user.company_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ offered_services: updatedServices })
      });
      if (res.ok) {
        setOfferedServices(updatedServices);
        toast.success(isUSA ? 'Service removed' : 'Servicio eliminado');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error removing service' : 'Error al eliminar servicio');
    } finally {
      setUpdating(false);
    }
  };

  // Loading states
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!user?.company_id) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">
          {isUSA ? 'No company registered' : 'Sin empresa registrada'}
        </h2>
        <p className="text-slate-500">
          {isUSA ? 'You need a company to access settings' : 'Necesitas tener una empresa para acceder a la configuración'}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        <span className="ml-2 text-slate-600">{isUSA ? 'Loading settings...' : 'Cargando configuración...'}</span>
      </div>
    );
  }

  const tabs = [
    { id: 'services', label: isUSA ? 'Services' : 'Servicios', icon: Settings },
    { id: 'privacy', label: isUSA ? 'Privacy' : 'Privacidad', icon: Lock },
    { id: 'authorizations', label: isUSA ? 'Authorizations' : 'Autorizaciones', icon: Shield },
    { id: 'team', label: isUSA ? 'Team' : 'Equipo', icon: Users },
    { id: 'preferences', label: isUSA ? 'Preferences' : 'Preferencias', icon: Sparkles }
  ];

  // Function to restart onboarding tour
  const handleRestartTour = () => {
    if (user?.email) {
      localStorage.removeItem(`texhub_tour_completed_${user.email}`);
      toast.success(isUSA 
        ? 'Tour restarted! Go to Dashboard to see it.' 
        : '¡Tour reiniciado! Ve al Dashboard para verlo.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-8 h-8 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {isUSA ? 'Company Settings' : 'Configuración de Empresa'}
          </h1>
          <p className="text-slate-500">{company?.company_name}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600' 
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="space-y-6">
          {/* Scheduling */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-blue-600" />
                {isUSA ? 'Scheduling Requests' : 'Solicitudes de Programación'}
              </CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Allow other users to request scheduling a meeting with you'
                  : 'Permite que otros usuarios soliciten agendar una cita o reunión contigo'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch 
                    checked={acceptsScheduling} 
                    onCheckedChange={handleToggleScheduling}
                    disabled={updating}
                  />
                  <span className="font-medium">
                    {acceptsScheduling 
                      ? (isUSA ? 'Enabled' : 'Activado') 
                      : (isUSA ? 'Disabled' : 'Desactivado')}
                  </span>
                </div>
                {acceptsScheduling && (
                  <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                    <CalendarCheck className="w-3 h-3 mr-1" />
                    {isUSA ? 'Visible on your profile' : 'Visible en tu perfil'}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-600" />
                WhatsApp Business
              </CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Add a WhatsApp button to your profile for direct contact'
                  : 'Agrega un botón de WhatsApp a tu perfil para que te contacten directamente'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch 
                    checked={whatsappEnabled} 
                    onCheckedChange={handleToggleWhatsapp}
                    disabled={updating}
                  />
                  <span className="font-medium">
                    {whatsappEnabled 
                      ? (isUSA ? 'Enabled' : 'Activado') 
                      : (isUSA ? 'Disabled' : 'Desactivado')}
                  </span>
                </div>
              </div>

              {whatsappEnabled && (
                <div className="flex gap-2 mt-4">
                  <Input
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    placeholder={isUSA ? 'Ex: 5215512345678' : 'Ej: 5215512345678'}
                    className="flex-1"
                  />
                  <Button onClick={handleSaveWhatsappNumber} disabled={updating}>
                    {isUSA ? 'Save' : 'Guardar'}
                  </Button>
                </div>
              )}

              {whatsappEnabled && whatsappNumber && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">
                        {isUSA ? 'WhatsApp active on your profile' : 'WhatsApp activo en tu perfil'}
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        {isUSA 
                          ? `Visitors will see a button to contact you via WhatsApp at ${whatsappNumber}`
                          : `Los visitantes verán un botón para contactarte por WhatsApp al número ${whatsappNumber}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Offered Services - for maquileros */}
          {company && (company.company_type === 'fabricante' || company.company_type === 'ambos') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-600" />
                  {isUSA ? 'Services You Offer' : 'Servicios que Ofreces'}
                </CardTitle>
                <CardDescription>
                  {isUSA 
                    ? 'Define the manufacturing services or processes you offer. Clients can select from this list when requesting a quote.'
                    : 'Define los servicios de maquila o procesos que ofreces. Los clientes podrán seleccionar de esta lista al solicitar cotización.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder={isUSA 
                      ? 'Ex: Fabric cutting, T-shirt manufacturing, Embroidery...'
                      : 'Ej: Corte de tela, Confección de playeras, Bordado...'}
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newService.trim()) {
                        e.preventDefault();
                        handleAddService();
                      }
                    }}
                  />
                  <Button 
                    onClick={handleAddService}
                    disabled={!newService.trim() || updating}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {isUSA ? 'Add' : 'Agregar'}
                  </Button>
                </div>

                {offeredServices.length > 0 ? (
                  <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-lg">
                    {offeredServices.map((service, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary"
                        className="py-1 px-3 text-sm flex items-center gap-1"
                      >
                        {service}
                        <button
                          onClick={() => handleRemoveService(index)}
                          className="ml-1 hover:text-red-600"
                          disabled={updating}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-500">
                    <p>{isUSA ? "You haven't added any services yet." : 'Aún no has agregado servicios.'}</p>
                    <p className="text-sm">{isUSA ? 'Add the manufacturing services you offer so clients can request quotes.' : 'Agrega los servicios de maquila que ofreces para que los clientes puedan cotizar.'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Privacy Tab */}
      {activeTab === 'privacy' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {catalogPrivate ? <Lock className="w-5 h-5 text-amber-600" /> : <Unlock className="w-5 h-5 text-emerald-600" />}
                {isUSA ? 'Catalog Privacy' : 'Privacidad del Catálogo'}
              </CardTitle>
              <CardDescription>
                {isUSA ? 'Control who can view your product catalog' : 'Controla quién puede ver tu catálogo de productos'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch 
                    checked={catalogPrivate} 
                    onCheckedChange={handleToggleCatalogPrivacy}
                    disabled={updating}
                  />
                  <span className="font-medium">
                    {isUSA 
                      ? `Catalog ${catalogPrivate ? 'Private' : 'Public'}`
                      : `Catálogo ${catalogPrivate ? 'Privado' : 'Público'}`}
                  </span>
                </div>
                <Badge variant={catalogPrivate ? "secondary" : "default"} className={catalogPrivate ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                  {catalogPrivate ? <Lock className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                  {catalogPrivate 
                    ? (isUSA ? 'Authorized users only' : 'Solo usuarios autorizados') 
                    : (isUSA ? 'Visible to everyone' : 'Visible para todos')}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Hide Contact Data Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {hideContactData ? <Eye className="w-5 h-5 text-amber-600 line-through" /> : <Eye className="w-5 h-5 text-emerald-600" />}
                {isUSA ? 'Contact Data' : 'Datos de Contacto'}
              </CardTitle>
              <CardDescription>
                {isUSA ? 'Control if your phone, email and website are visible on your public profile' : 'Controla si tu teléfono, email y sitio web son visibles en tu perfil público'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch 
                    checked={hideContactData} 
                    onCheckedChange={handleToggleHideContactData}
                    disabled={updating}
                  />
                  <span className="font-medium">
                    {hideContactData 
                      ? (isUSA ? 'Data hidden' : 'Datos ocultos') 
                      : (isUSA ? 'Data visible' : 'Datos visibles')}
                  </span>
                </div>
                <Badge variant={hideContactData ? "secondary" : "default"} className={hideContactData ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                  {hideContactData ? <Lock className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                  {hideContactData 
                    ? (isUSA ? 'Message only' : 'Solo por mensaje') 
                    : (isUSA ? 'Visible to everyone' : 'Visible para todos')}
                </Badge>
              </div>

              {hideContactData && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">{isUSA ? 'Protection enabled' : 'Protección activada'}</p>
                      <p className="text-sm text-amber-700 mt-1">
                        {isUSA 
                          ? 'Visitors will see a "Request contact data" button on your profile. You will receive requests and can approve or reject them.'
                          : 'Los visitantes verán un botón de "Solicitar datos de contacto" en tu perfil. Tú recibirás las solicitudes y podrás aprobarlas o rechazarlas.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Access Requests */}
          {(contactAccessRequests.length > 0 || catalogAccessRequests.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  {isUSA ? 'Pending Access Requests' : 'Solicitudes de Acceso Pendientes'}
                </CardTitle>
                <CardDescription>
                  {isUSA 
                    ? 'Review and respond to access requests from other companies'
                    : 'Revisa y responde a las solicitudes de acceso de otras empresas'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Contact Access Requests */}
                {contactAccessRequests.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {isUSA ? 'Contact Data Requests' : 'Solicitudes de Datos de Contacto'}
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                        {contactAccessRequests.length}
                      </Badge>
                    </h4>
                    <div className="space-y-2">
                      {contactAccessRequests.map(req => (
                        <div key={req.request_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">{req.requester_company?.company_name || 'Empresa'}</p>
                              <p className="text-sm text-slate-500">
                                {req.requester_company?.location_city && req.requester_company?.location_state
                                  ? `${req.requester_company.location_city}, ${req.requester_company.location_state}`
                                  : (isUSA ? 'Location not available' : 'Ubicación no disponible')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleContactAccessResponse(req.request_id, 'reject')}
                              disabled={processingRequest === req.request_id}
                            >
                              {processingRequest === req.request_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </Button>
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleContactAccessResponse(req.request_id, 'approve')}
                              disabled={processingRequest === req.request_id}
                            >
                              {processingRequest === req.request_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Catalog Access Requests */}
                {catalogAccessRequests.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      {isUSA ? 'Catalog Access Requests' : 'Solicitudes de Acceso al Catálogo'}
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        {catalogAccessRequests.length}
                      </Badge>
                    </h4>
                    <div className="space-y-2">
                      {catalogAccessRequests.map(req => (
                        <div key={req.request_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium">{req.requester_company_name || req.requester_company?.company_name || 'Empresa'}</p>
                              {req.message && (
                                <p className="text-sm text-slate-500 italic">"{req.message}"</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleCatalogAccessResponse(req.request_id, 'reject')}
                              disabled={processingRequest === req.request_id}
                            >
                              {processingRequest === req.request_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </Button>
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleCatalogAccessResponse(req.request_id, 'approve')}
                              disabled={processingRequest === req.request_id}
                            >
                              {processingRequest === req.request_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Authorizations Tab - Replaces old Links Tab */}
      {activeTab === 'authorizations' && (
        <div className="space-y-6">
          {/* Contact Data Authorizations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-600" />
                {isUSA ? 'Contact Data Authorizations' : 'Autorizaciones de Datos de Contacto'}
                {givenContactAuthorizations.length > 0 && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {givenContactAuthorizations.length}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Companies that can see your contact information'
                  : 'Empresas que pueden ver tu información de contacto'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {givenContactAuthorizations.length === 0 ? (
                <p className="text-slate-500 text-center py-6">
                  {isUSA 
                    ? 'No companies have access to your contact data yet.'
                    : 'Ninguna empresa tiene acceso a tus datos de contacto aún.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {givenContactAuthorizations.map(auth => (
                    <div key={auth.request_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{auth.requester_company?.company_name || 'Empresa'}</p>
                          <p className="text-sm text-slate-500">
                            {auth.requester_company?.location_city && auth.requester_company?.location_state
                              ? `${auth.requester_company.location_city}, ${auth.requester_company.location_state}`
                              : (isUSA ? 'Location not available' : 'Ubicación no disponible')}
                          </p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleRevokeContactAuthorization(auth.request_id)}
                        disabled={revokingAuthorization === auth.request_id}
                      >
                        {revokingAuthorization === auth.request_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-1" />
                            {isUSA ? 'Revoke' : 'Revocar'}
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Catalog Authorizations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-indigo-600" />
                {isUSA ? 'Catalog Authorizations' : 'Autorizaciones de Catálogo'}
                {givenCatalogAuthorizations.length > 0 && (
                  <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
                    {givenCatalogAuthorizations.length}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Companies that can see your private catalog'
                  : 'Empresas que pueden ver tu catálogo privado'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {givenCatalogAuthorizations.length === 0 ? (
                <p className="text-slate-500 text-center py-6">
                  {isUSA 
                    ? 'No companies have access to your catalog yet.'
                    : 'Ninguna empresa tiene acceso a tu catálogo aún.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {givenCatalogAuthorizations.map(auth => (
                    <div key={auth.request_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium">{auth.requester_company?.company_name || auth.requester_company_name || 'Empresa'}</p>
                          <p className="text-sm text-slate-500">
                            {auth.requester_company?.location_city && auth.requester_company?.location_state
                              ? `${auth.requester_company.location_city}, ${auth.requester_company.location_state}`
                              : (isUSA ? 'Location not available' : 'Ubicación no disponible')}
                          </p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleRevokeCatalogAuthorization(auth.request_id)}
                        disabled={revokingAuthorization === auth.request_id}
                      >
                        {revokingAuthorization === auth.request_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-1" />
                            {isUSA ? 'Revoke' : 'Revocar'}
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">
                    {isUSA ? 'About Authorizations' : 'Acerca de las Autorizaciones'}
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    {isUSA 
                      ? 'When you revoke an authorization, the company will no longer be able to see your protected data. They can request access again if needed.'
                      : 'Cuando revocas una autorización, la empresa ya no podrá ver tus datos protegidos. Pueden solicitar acceso nuevamente si lo necesitan.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                {isUSA ? 'Team Members' : 'Miembros del Equipo'}
              </CardTitle>
              <CardDescription>
                {isUSA ? 'Manage who has access to your company account' : 'Gestiona quién tiene acceso a tu cuenta de empresa'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {members.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  {isUSA ? 'There are no members in your team yet.' : 'Aún no hay miembros en tu equipo.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {members.map(member => (
                    <div key={member.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                      </div>
                      {member.is_admin && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                          <Crown className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="space-y-6">
          {/* Onboarding Tour */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                {isUSA ? 'Interactive Tour' : 'Tour Interactivo'}
              </CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Restart the guided tour to learn about all TexHub features'
                  : 'Reinicia el tour guiado para conocer todas las funciones de TexHub'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <RotateCcw className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">
                      {isUSA ? 'Platform Tour' : 'Tour de la Plataforma'}
                    </p>
                    <p className="text-sm text-slate-500">
                      {isUSA 
                        ? 'A quick walkthrough of the main features'
                        : 'Un recorrido rápido por las funciones principales'}
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleRestartTour}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {isUSA ? 'Restart Tour' : 'Reiniciar Tour'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Language & Region (informative) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-600" />
                {isUSA ? 'Language & Region' : 'Idioma y Región'}
              </CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Your region setting determines the language and currency displayed'
                  : 'Tu configuración de región determina el idioma y moneda mostrados'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  {isUSA 
                    ? 'You can change your region using the selector in the top navigation bar. This affects prices, currency, and interface language.'
                    : 'Puedes cambiar tu región usando el selector en la barra de navegación superior. Esto afecta precios, moneda e idioma de la interfaz.'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CompanySettingsPage;
